"use client";

import { Car, CalendarDays, Clock, MapPin } from "lucide-react";
import { formatConsistentDate } from "@/utils/timezone-date-utils";
import { quoteThemeCssVars } from "../quote-theme/quote-theme-css-vars";
import { iTransferLegDetails } from "@/@sdk/holiday-quotes-x/holiday-quotes-x-model";
import { ImageGallery } from "@/components/common/image-gallery/image-gallery";
import { HtmlContentWithReadMore } from "@/components/common/html-content-with-read-more";

export interface TransferItineraryInnerProps
  extends iTransferLegDetails {
  primaryColor?: string;
  day?: number;
}

export function TransferItineraryInner(props: TransferItineraryInnerProps) {
  const primaryColor = quoteThemeCssVars.primary;
  const lightColor = quoteThemeCssVars.primary10;

  const formatDate = (date: string | null) => {
    if (!date) return "";
    return formatConsistentDate(date, "EEE, d MMM yyyy");
  };

  return (
    <div className="p-4 sm:p-6">
      {/* Transfer Header */}
      <div className="mb-4 sm:mb-6">
        <h3 className="text-lg sm:text-xl font-semibold mb-2">{props.provider}</h3>
        <span
          className="inline-flex items-center px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm"
          style={{
            backgroundColor: lightColor,
            color: primaryColor,
          }}
        >
          {props.transferType}
        </span>
      </div>

      {/* Overview/Body */}
      {props.body && (
        <HtmlContentWithReadMore
          html={props.body}
          className="mb-4 sm:mb-6"
          maxWords={40}
        />
      )}

      {/* Gallery */}
      {props.gallery && props.gallery.length > 0 && (
        <div className="mb-4 sm:mb-6">
          <ImageGallery images={props.gallery} />
        </div>
      )}

      {/* Transfer Details */}
      <div className="space-y-4 sm:space-y-6">
        {/* Date & Time */}
        <div className="flex items-start gap-2 sm:gap-3">
          <CalendarDays
            className="w-4 h-4 sm:w-5 sm:h-5 mt-1"
            style={{ color: primaryColor }}
          />
          <div>
            <div className="font-medium text-sm sm:text-base">
              <span className="hidden sm:inline">Transfer Date</span>
              <span className="sm:hidden">Date</span>
            </div>
            <div className="text-sm sm:text-base">{formatDate(props.transferDate)}</div>
            {props.pickUpTime && (
              <div className="flex items-center gap-1 text-sm text-gray-600 mt-1">
                <Clock className="w-3 h-3 sm:w-4 sm:h-4" style={{ color: primaryColor }} />
                <span className="hidden sm:inline">Pick-up at </span>
                <span className="sm:hidden">Pick-up: </span>
                {props.pickUpTime}
              </div>
            )}
          </div>
        </div>

        {/* Locations */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-start gap-2 sm:gap-3">
            <MapPin
              className="w-4 h-4 sm:w-5 sm:h-5 mt-1"
              style={{ color: primaryColor }}
            />
            <div>
              <div className="font-medium text-sm sm:text-base">
                <span className="hidden sm:inline">Pick-up</span>
                <span className="sm:hidden">From</span>
              </div>
              <div className="text-sm text-gray-600">{props.pickUpLocation}</div>
            </div>
          </div>

          <div className="flex items-start gap-2 sm:gap-3">
            <MapPin
              className="w-4 h-4 sm:w-5 sm:h-5 mt-1"
              style={{ color: primaryColor }}
            />
            <div>
              <div className="font-medium text-sm sm:text-base">
                <span className="hidden sm:inline">Drop-off</span>
                <span className="sm:hidden">To</span>
              </div>
              <div className="text-sm text-gray-600">{props.dropOffLocation}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
